import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { CustomModal, ReactTable } from '@components';
import { Box, Typography } from '@mui/material';
import { MRT_ToggleGlobalFilterButton } from 'material-react-table';
import { apiBuySetPriceProfile } from '@store';
import { showToast } from '@utils';
import { useDispatch } from 'react-redux';

const Buy = forwardRef(
    ({ open, handleClose, singlePriceProfile, eur, chf }, ref) => {
        const dispatch = useDispatch();

        const [timer, setTimer] = useState(20);
        const [submitting, setSubmitting] = useState(false);

        // state for unmatched symbols
        const [unmatchedSymbols, setUnmatchedSymbols] = useState([]);

        useEffect(() => {
            const interval = setInterval(() => {
                setTimer((timer) => timer - 1);
            }, 1000);
            return () => clearInterval(interval);
        }, [timer]);

        useEffect(() => {
            if (timer === 0) {
                handleClose();
            }
        }, [timer, handleClose]);

        useEffect(() => {
            if (open) {
                setTimer(20);
            }
        }, [open]);

        useEffect(() => {
            if (unmatchedSymbols.length) {
                showToast(
                    'warning',
                    `The following symbols were not found in the data: ${unmatchedSymbols.join(
                        ', '
                    )}`,
                    'top-right',
                    { autoClose: 10000 }
                );
            }
        }, []);

        const sortedData = useMemo(() => {
            if (singlePriceProfile?.data) {
                // Determine which symbols to use based on currency
                const currency = singlePriceProfile.currency?.toLowerCase();
                const symbols =
                    currency === 'chf' ? chf?.symbols : eur?.symbols;

                if (!symbols) return singlePriceProfile.data; // Return all if no symbols defined

                const contract = [];
                const noDataSymbols = new Set(); // Use a Set to track symbols not found in data
                singlePriceProfile.data.forEach((data) => {
                    const found = symbols.find((symbol) => {
                        if (data.name === symbol) {
                            return true;
                        }
                    });

                    if (!found) {
                        noDataSymbols.add(data.name); // Add unmatched symbol to the Set
                    } else {
                        contract.push(data);
                    }
                });
                setUnmatchedSymbols(Array.from(noDataSymbols));
                return contract;
            }
            return [];
        }, [singlePriceProfile, eur, chf]);

        const columns = useMemo(
            () => [
                {
                    header: 'Contract',
                    id: 'name',
                    accessorFn: ({ name }) => name || '',
                    Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                    size: 50
                },
                {
                    header: 'Volume [MWh/h]',
                    id: 'volume',
                    accessorFn: ({ volume }) =>
                        String(volume)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') ||
                        '',
                    Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                    size: 50
                },
                {
                    header: 'Total Volume [MWh]',
                    id: 'mwh',
                    accessorFn: ({ mwh }) =>
                        String(mwh)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') ||
                        '',
                    Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                    size: 50
                },
                {
                    header: 'Price',
                    id: 'askprice',
                    accessorFn: ({ askprice }) =>
                        String(parseFloat(askprice).toFixed(3))?.replace(
                            /\B(?=(\d{3})+(?!\d))/g,
                            ','
                        ) || '',
                    Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                    size: 50
                },
                {
                    header: 'Amount',
                    id: 'asktotal',
                    accessorFn: ({ asktotal }) =>
                        String(parseFloat(asktotal).toFixed(3))?.replace(
                            /\B(?=(\d{3})+(?!\d))/g,
                            ','
                        ) || '',
                    Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                    size: 50
                }
            ],
            []
        );

        const handleBuyProfile = async (e) => {
            e.preventDefault();
            if (submitting) return;
            setSubmitting(true);
            if (
                window.confirm(
                    `Are you sure you want to buy ${singlePriceProfile.name} TTF profile?`
                )
            ) {
                const resp = await dispatch(
                    apiBuySetPriceProfile(singlePriceProfile)
                );
                if (resp?.meta?.requestStatus === 'fulfilled') {
                    handleClose();
                    showToast('success', 'TTF Profile bought successfully');
                } else if (resp?.meta?.requestStatus === 'rejected') {
                    showToast(
                        'error',
                        resp.payload || 'Failed to buy TTF profile'
                    );
                }
            }
            setSubmitting(false);
        };

        return (
            <CustomModal
                handleClose={handleClose}
                handleSubmit={handleBuyProfile}
                modalTitle={`TTF Profile - Buy Window will be closed in ${timer} seconds`}
                buttonText={'Buy'}
                disabled={submitting}
                ref={ref}
            >
                <Box>
                    <ReactTable
                        renderToolbarInternalActions={({ table }) => (
                            <div onMouseDown={(e) => e.stopPropagation()}>
                                <MRT_ToggleGlobalFilterButton table={table} />
                            </div>
                        )}
                        headerText={'TTF Contracts'}
                        columns={columns || []}
                        data={sortedData || []}
                    />
                </Box>
                <Box className="flex gap-2 mt-4">
                    <Box>
                        <Typography sx={{ color: '#000' }}>Currency</Typography>
                        <Typography sx={{ color: '#000' }}>
                            Total MWh
                        </Typography>
                        <Typography sx={{ color: '#000' }}>
                            Total Amount
                        </Typography>
                        <Typography sx={{ color: '#000' }}>
                            Profile Price
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ textTransform: 'uppercase' }}>
                            : {singlePriceProfile?.currency}
                        </Typography>
                        <Typography>
                            : {String(singlePriceProfile?.totalmwh)}
                        </Typography>
                        <Typography>
                            :{' '}
                            {String(
                                parseFloat(
                                    singlePriceProfile?.totalprofileprice
                                ).toFixed(3)
                            )?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        </Typography>
                        <Typography>
                            :{' '}
                            {String(
                                parseFloat(
                                    singlePriceProfile?.profileprice
                                ).toFixed(3)
                            )?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        </Typography>
                    </Box>
                </Box>
            </CustomModal>
        );
    }
);

export default Buy;
